import { Component, EventEmitter, Inject, Output } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe, KeyValuePipe, NgClass, NgFor, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault, UpperCasePipe } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { map } from 'rxjs';
import { QRCodeModule } from 'angularx-qrcode';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { AngularYandexMapsModule, YaReadyEvent } from 'angular8-yandex-maps';
import { YaLocationService } from '../../services/ya-location.service';
import { CopyLoaderComponent } from '../copy-loader/copy-loader.component';
import { AuthService } from '../../services/auth.service';
import { Tfa } from '../../interfaces/tfa.interface';
import { CutStringPipe } from '../../pipes/cut-string.pipe';
import { environment } from '../../environments/environment';

export interface ConnectDeviceData {
  account: string;
  address1: string;
  address2: string;
  address3: string;
}

@Component({
  standalone: true,
  selector: 'wm-libs-generic-dialog',
  templateUrl: './generic-dialog.component.html',
  styleUrls: ['./generic-dialog.component.scss'],
  imports: [
    NgSwitch,
    NgIf,
    NgClass,
    AsyncPipe,
    NgSwitchCase,
    MatFormFieldModule,
    TranslateModule,
    QRCodeModule,
    MatInputModule,
    NgFor,
    MatSelectModule,
    MatDialogModule,
    MatIconModule,
    MatIcon,
    MatButtonModule,
    ReactiveFormsModule,
    FormsModule,
    MatCheckboxModule,
    NgSwitchDefault,
    NgSwitchCase,
    NgSwitch,
    UpperCasePipe,
    NgxMaskDirective,
    AngularYandexMapsModule,
    CopyLoaderComponent,
    CutStringPipe,
    KeyValuePipe,
  ],
  providers: [provideNgxMask({ thousandSeparator: ',', decimalMarker: '.' })],
})
export class GenericDialogComponent {
  form?: FormGroup;
  @Output() formValueChange = new EventEmitter<any>();
  @Output() formSubmited = new EventEmitter<{ submited: boolean; formValue: any }>();

  isMobile$ = this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(map((result) => result.matches));

  // ya-map
  mapState: ymaps.IMapState = {
    controls: ['zoomControl', 'fullscreenControl', 'searchControl'],
    behaviors: ['drag', 'dblClickZoom', 'multiTouch', 'rightMouseButtonMagnifier'],
  };
  public fromClick = false;
  public map: any;
  tfaSecretKey!: Tfa;

  //connect pool device
  connectData!: ConnectDeviceData;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<GenericDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private yaLocationService: YaLocationService,
    private authService: AuthService
  ) {
    if (data.fields) {
      this.form = this.createForm(data.fields);
      this.form.valueChanges.subscribe((value) => this.formValueChange.emit(value));

      const tfaDialog = data.fields.find((item: any) => item.type === 'qrcode');

      if (tfaDialog) {
        this.getTfaSecretKey();
      }
    }

    this.connectData = data?.data;
  }

  createForm(fields: any[]): FormGroup {
    const group: any = {};
    fields.forEach((field) => {
      group[field.name] = [{ value: field.value || '', disabled: field?.disabled === true || false }, field.validators || []];
    });
    return this.fb.group(group);
  }

  onSubmit() {
    if (this.form && this.form?.valid) {
      if (this.data.autoClose) {
        this.dialogRef.close(this.form.value);
      } else {
        this.formSubmited.emit({ submited: true, formValue: this.form.value });
      }
    } else if (!this.form) {
      this.dialogRef.close(true);
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  // add location in ya-map
  yaclick(e: any) {
    this.fromClick = true;
    this.map.controls.get('searchControl').clear();
    this.yaLocationService.getLocationNameByCoords(e.event.get('coords').reverse()).subscribe((res: any) => {
      this.form?.patchValue({
        latitude: e.event.get('coords')[0],
        longitude: e.event.get('coords')[1],
        name: res.response.GeoObjectCollection.featureMember[0]?.GeoObject?.name,
      });
    });
  }

  balloonopen(e: any) {
    e.target.balloon.close();
    this.fromClick = false;

    this.form?.patchValue({
      latitude: e.event.get('coords')[0],
      longitude: e.event.get('coords')[1],
      name: this.map.controls.get('searchControl').getRequestString(),
    });
  }

  onMapReady(event: YaReadyEvent<ymaps.Map>): void {
    this.map = event.target;
  }

  getTfaSecretKey() {
    this.authService.getTfaSecretKey().subscribe((res) => {
      this.tfaSecretKey = res;
    });
  }

  openLearnMore() {
    this.dialogRef.close();
    window.location.href = environment.techDomain + '/support';
  }
}
